// Atualizar os precos dos produtos a partir da MEDIA da familia (DA-642).
//
// O trabalho que isto substitui: toda semana o Ruan lia o Monitor e digitava, produto a produto, o
// custo e o PIX no cadastro — medido, ~62 produtos de cabo, todos seguindo `preco_pix = custo x
// markup`. O que faltava nao era o botao: era a REGUA, a lista de quais produtos cada familia
// precifica e em que fator (metro = 1, rolo de 100m = 100). Por isso a regua mora aqui dentro, no
// mesmo modal que aplica — a ferramenta que resolve fica na tela onde o problema aparece.
//
// A previa vem PRONTA do servidor (mesma funcao que grava), entao o numero conferido e o numero
// gravado. Nada aqui recalcula preco por conta propria.
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { Trash2, Plus, Search } from 'lucide-react';
import { api } from '../../lib/api';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';
import { MSG_PRODUTO_INATIVO, produtoInativo, SeloInativo } from '../../lib/produto-inativo';
import { MoneyInput } from '../../components/inputs/MoneyInput';
import { useListaTeclado } from '../../lib/useListaTeclado';
import { contar, formatMoedaSemSimbolo } from '@fiodecobre/shared';

interface LinhaPrevia {
  vinculo_id: number;
  produto_id: number;
  codigo_interno: string | null;
  descricao: string | null;
  unidade: string | null;
  fator: number;
  custo_atual: number;
  custo_novo: number;
  pix_atual: number;
  pix_novo: number;
  cartao_novo: number;
  muda: boolean;
}

interface PreviaApi {
  monitor_item_id: number;
  nome: string;
  markup: number;
  ofertas_usadas: number;
  ofertas_total: number;
  media_custo: number;
  media_sugestao: number;
  pix_unidade: number;
  coleta_mais_antiga: string | null;
  coleta_mais_nova: string | null;
  linhas: LinhaPrevia[];
}

interface ProdutoBusca { id: number; codigo_interno: string; descricao: string; status?: string | null }

const num: CSSProperties = { fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' };
// Contorno da tabela e divisoria antes do Custo novo — o tom do traco do desenho dele.
const CONTORNO = '#f59e0b';
const m = (v: number | null | undefined, c = 2) => (v == null ? '—' : formatMoedaSemSimbolo(v, c));
const DIAS_COLETA_VELHA = 14;

export function ModalAtualizarPrecos({
  monitorItemId, nome, fornecedorId = null, fornecedorNome = null, produtoPrincipal = null, aberto, onFechar, onAplicado,
}: {
  monitorItemId: number;
  nome: string;
  // Vinculo da familia (o "1907" do cabecalho). Com a regua VAZIA ele entra sozinho — pedido dele,
  // 07/10/2026: "por que tenho que repetir o codigo se ele ja esta vinculado como produto principal?"
  produtoPrincipal?: { id: number; codigo_interno: string | null } | null;
  // Media de UM fornecedor (a linha de media dele no rodape da familia). null = todos.
  fornecedorId?: number | null;
  fornecedorNome?: string | null;
  aberto: boolean;
  onFechar: () => void;
  onAplicado: () => void;
}) {
  const toast = useToast();
  const [grao, setGrao] = useState<'centavo' | 'dez_centavos'>('centavo');
  const [previa, setPrevia] = useState<PreviaApi | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  // Quais linhas entram na gravacao. Comeca com todas as que mudam — desmarcar e a excecao.
  const [marcados, setMarcados] = useState<Set<number>>(new Set());
  // PIX digitado por produto (pedido dele, 07/10/2026). Vale no lugar do calculado; vazio volta ao
  // calculado. Sobrevive a recarga da previa (trocar fator, arredondamento).
  const [pixDigitado, setPixDigitado] = useState<Map<number, number>>(new Map());

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await api.get<PreviaApi>(`/api/monitor-precos/itens/${monitorItemId}/previa-precos`, {
        query: { arredondamento: grao, ...(fornecedorId != null ? { fornecedor_id: fornecedorId } : {}) },
      });
      setPrevia(r);
      setErro(null);
      setMarcados(new Set(r.linhas.filter((l) => l.muda).map((l) => l.produto_id)));
    } catch (e) {
      setPrevia(null);
      setErro((e as Error).message);
    } finally {
      setCarregando(false);
    }
  }, [monitorItemId, grao, fornecedorId]);

  useEffect(() => { if (aberto) void carregar(); }, [aberto, carregar]);

  // Regua vazia + familia com produto principal = o principal entra na regua sozinho, UMA vez por
  // abertura. So com unidade MT (fator 1, sem ambiguidade): a [DA-643] p.3 ja gravou preco de metro
  // num ROLO pelo fator errado, entao produto em outra unidade fica de fora e aparece o aviso abaixo
  // pra ele vincular com o fator na mao. Vincular nao mexe em preco — isso continua no Aplicar.
  const principalTentado = useRef(false);
  const [principalOutraUnidade, setPrincipalOutraUnidade] = useState<string | null>(null);
  useEffect(() => {
    if (!previa || previa.linhas.length > 0 || !produtoPrincipal || principalTentado.current) return;
    principalTentado.current = true;
    void (async () => {
      try {
        const q = produtoPrincipal.codigo_interno ?? String(produtoPrincipal.id);
        const lista = await api.get<{ id: number; codigo_interno: string; descricao: string; unidade_sigla: string | null }[]>('/api/produtos', { query: { q } });
        const p = (lista || []).find((x) => x.id === produtoPrincipal.id);
        if (!p) return;
        if ((p.unidade_sigla ?? '').toUpperCase() === 'MT') await vincular(p, 1);
        else setPrincipalOutraUnidade(p.unidade_sigla ?? 'sem unidade');
      } catch { /* sem o automatico, a busca abaixo continua valendo */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previa, produtoPrincipal]);

  async function vincular(p: ProdutoBusca, fator: number) {
    try {
      await api.post(`/api/monitor-precos/itens/${monitorItemId}/regua`, { produto_id: p.id, fator });
      await carregar();
    } catch (e) { toast.erro((e as Error).message); }
  }

  async function trocarFator(vinculoId: number, fator: number) {
    if (!Number.isFinite(fator) || fator <= 0) return;
    try {
      await api.put(`/api/monitor-precos/regua/${vinculoId}`, { fator });
      await carregar();
    } catch (e) { toast.erro((e as Error).message); }
  }

  async function desvincular(vinculoId: number) {
    try {
      await api.delete(`/api/monitor-precos/regua/${vinculoId}`);
      await carregar();
    } catch (e) { toast.erro((e as Error).message); }
  }

  async function aplicar() {
    if (!previa) return;
    setSalvando(true);
    try {
      const r = await api.post<{ atualizados: number }>(`/api/monitor-precos/itens/${monitorItemId}/aplicar-precos`, {
        arredondamento: grao,
        produto_ids: [...marcados],
        fornecedor_id: fornecedorId,
        pix_digitado: [...pixDigitado].map(([produto_id, pix]) => ({ produto_id, pix })),
      });
      toast.sucesso(`${contar(r.atualizados, 'produto')} com preco atualizado.`);
      onAplicado();
      onFechar();
    } catch (e) {
      toast.erro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  }

  // Mesma regra que o servidor aplica ao gravar: o digitado troca o PIX e decide se a linha muda.
  const linhas = (previa?.linhas ?? []).map((l) => {
    const pix = pixDigitado.get(l.produto_id);
    if (pix == null) return { ...l, pix_calculado: l.pix_novo, digitado: false };
    return { ...l, pix_calculado: l.pix_novo, pix_novo: pix, digitado: true, muda: l.custo_novo !== l.custo_atual || pix !== l.pix_atual };
  });

  function digitarPix(l: LinhaPrevia, calculado: number, v: number) {
    const valor = Math.round(v * 100) / 100;
    setPixDigitado((s) => {
      const n = new Map(s);
      if (!(valor > 0) || valor === calculado) n.delete(l.produto_id); else n.set(l.produto_id, valor);
      return n;
    });
    // Digitar um preco na linha e o gesto de quem quer grava-la — sem isto o valor so valeria
    // depois de um segundo clique na caixa, que e o que se esquece.
    if (valor > 0 && valor !== calculado) setMarcados((s) => new Set(s).add(l.produto_id));
  }
  const aMudar = linhas.filter((l) => l.muda && marcados.has(l.produto_id));
  // Botao apagado e mudo e beco sem saida — a linha abaixo dele diz o que falta.
  const impedimento = erro
    ? erro
    : linhas.length === 0
      ? 'Vincule ao menos um produto abaixo para dizer quem esta familia precifica.'
      : aMudar.length === 0
        ? 'Nenhum produto marcado tem preco diferente do atual.'
        : null;

  const diasColeta = previa?.coleta_mais_antiga
    ? Math.floor((Date.now() - new Date(previa.coleta_mais_antiga).getTime()) / 86400000)
    : null;

  const th: CSSProperties = {
    padding: '6px 8px', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.3,
    color: 'var(--panel-fg, #1f2937)', borderBottom: '1px solid var(--panel-border)', whiteSpace: 'nowrap',
  };
  const td: CSSProperties = { padding: '7px 8px', borderTop: '1px solid var(--panel-border)', whiteSpace: 'nowrap' };

  return (
    <Modal
      aberto={aberto}
      onFechar={onFechar}
      titulo={`Atualizar precos — ${nome}${fornecedorNome ? ` — media ${fornecedorNome}` : ''}`}
      largura={980}
      rodape={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
          <span style={{ fontSize: 11.5, color: 'var(--panel-fg, #1f2937)', flex: 1, textAlign: 'left' }}>
            {impedimento ?? `Vai gravar em ${contar(aMudar.length, 'produto')}.`}
          </span>
          <button type="button" className="btn-secundario" onClick={onFechar}>Cancelar</button>
          <button
            type="button"
            className="btn-primario"
            disabled={!!impedimento || salvando || carregando}
            onClick={() => void aplicar()}
            style={{ whiteSpace: 'nowrap' }}
          >
            {salvando ? 'Gravando...' : `Aplicar em ${aMudar.length}`}
          </button>
        </div>
      }
    >
      {/* O que a conta usou — o MESMO numero que o rodape da familia imprime, vindo do servidor. */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'baseline',
        padding: '10px 12px', borderRadius: 8, background: 'var(--page-bg)', marginBottom: 12,
      }}>
        {previa ? (
          <>
            <span style={{ fontSize: 11.5 }}>
              Custo medio <b style={{ ...num, fontSize: 14 }}>{m(previa.media_custo, 4)}</b>
              <span style={{ fontSize: 11 }}> · {contar(previa.ofertas_usadas, 'opcao', 'opcoes')} com preco de {previa.ofertas_total}{fornecedorNome ? ` da ${fornecedorNome}` : ''}</span>
            </span>
            <span style={{ fontSize: 11.5 }}>
              Sugestao <b style={{ ...num, fontSize: 14 }}>{m(previa.media_sugestao, 4)}</b>
              <span style={{ fontSize: 11 }}> (markup {Math.round(previa.markup * 100)}%)</span>
            </span>
            <span style={{ fontSize: 11.5 }}>
              PIX da unidade <b style={{ ...num, fontSize: 14, color: 'var(--num-destaque, #1e3a8a)' }}>{m(previa.pix_unidade, 2)}</b>
            </span>
            <label style={{ fontSize: 11.5, display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
              Arredondar pra cima em
              <select value={grao} onChange={(e) => setGrao(e.target.value as typeof grao)} style={{ fontSize: 11.5, padding: '3px 6px' }}>
                <option value="centavo">1 centavo</option>
                <option value="dez_centavos">10 centavos</option>
              </select>
            </label>
          </>
        ) : (
          <span style={{ fontSize: 12 }}>{carregando ? 'Calculando...' : (erro ?? '—')}</span>
        )}
      </div>

      {/* Coleta velha nao impede — avisa. Aplicar preco com custo de semanas atras e pior que nao
          aplicar, e so quem esta na frente da tela sabe se aquele custo ainda vale. */}
      {diasColeta != null && diasColeta > DIAS_COLETA_VELHA && (
        <div style={{
          background: '#f97316', color: '#000', fontWeight: 600, fontSize: 11.5,
          padding: '7px 12px', borderRadius: 8, marginBottom: 12,
        }}>
          A coleta mais antiga desta familia tem {contar(diasColeta, 'dia')}. Vale atualizar os precos do fornecedor antes.
        </div>
      )}

      <div style={{ fontSize: 11.5, color: 'var(--panel-fg, #1f2937)', marginBottom: 6 }}>
        <b>Produtos que esta familia precifica.</b> O <b>fator</b> diz quantas unidades da familia cabem
        no produto — metro = 1, rolo de 100m = 100. O PIX do rolo sai do PIX do metro x fator.
      </div>

      {/* Moldura amarela + divisoria antes do CUSTO NOVO (pedido dele com desenho, 07/10/2026 — ela
          estava antes do PIX novo e mudou de lugar quando as colunas foram reordenadas): separa o que
          vale HOJE (Custo/PIX atual) do que e NOVO (Custo novo, PIX novo, Lucro %). */}
      <div style={{ border: `2px solid ${CONTORNO}`, borderRadius: 8, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 34 }}></th>
              <th style={{ ...th, textAlign: 'left' }}>Codigo</th>
              <th style={{ ...th, textAlign: 'left' }}>Produto</th>
              <th style={th}>Un</th>
              <th style={th}>Fator</th>
              <th style={{ ...th, textAlign: 'right' }}>Custo atual</th>
              {/* Ordem pedida por ele (07/10/2026): os dois de HOJE juntos, depois os dois NOVOS. */}
              <th style={{ ...th, textAlign: 'right' }}>PIX atual</th>
              <th style={{ ...th, textAlign: 'right' }}>Lucro atual</th>
              <th style={{ ...th, textAlign: 'right', borderLeft: `2px solid ${CONTORNO}` }}>Custo novo</th>
              <th style={{ ...th, textAlign: 'right' }}>PIX novo</th>
              <th style={{ ...th, textAlign: 'right' }}>Lucro %</th>
              <th style={{ ...th, width: 34 }}></th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => {
              const marcado = marcados.has(l.produto_id);
              const apagado = !l.muda;
              return (
                <tr key={l.vinculo_id} style={apagado ? { color: 'var(--panel-fg, #1f2937)', opacity: 0.55 } : undefined}>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={marcado}
                      disabled={!l.muda}
                      onChange={(e) => setMarcados((s) => {
                        const n = new Set(s);
                        if (e.target.checked) n.add(l.produto_id); else n.delete(l.produto_id);
                        return n;
                      })}
                    />
                  </td>
                  <td style={{ ...td, ...num, textAlign: 'left' }}>{l.codigo_interno ?? '—'}</td>
                  <td style={{ ...td, whiteSpace: 'normal', maxWidth: 280, fontSize: 12 }}>{l.descricao ?? '—'}</td>
                  <td style={{ ...td, textAlign: 'center', fontSize: 11.5 }}>{l.unidade ?? '—'}</td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <input
                      type="number"
                      min={0.0001}
                      step={1}
                      defaultValue={l.fator}
                      onBlur={(e) => {
                        const v = Number(e.target.value);
                        if (v !== l.fator) void trocarFator(l.vinculo_id, v);
                      }}
                      style={{ width: 70, ...num, fontSize: 12, padding: '3px 6px', textAlign: 'right' }}
                    />
                  </td>
                  <td style={{ ...td, ...num, textAlign: 'right' }}>{m(l.custo_atual, 4)}</td>
                  <td style={{ ...td, ...num, textAlign: 'right' }}>{m(l.pix_atual, 2)}</td>
                  {/* Lucro atual (pedido dele, 07/10/2026): a mesma conta do Lucro % da direita, com os
                      dois numeros de HOJE desta linha — (PIX atual / Custo atual) - 1. */}
                  <td style={{ ...td, ...num, textAlign: 'right' }}>
                    {l.custo_atual > 0
                      ? `${((l.pix_atual / l.custo_atual - 1) * 100).toFixed(2).replace('.', ',')}%`
                      : '—'}
                  </td>
                  <td style={{ ...td, ...num, textAlign: 'right', fontWeight: l.muda ? 700 : 400, borderLeft: `2px solid ${CONTORNO}` }}>{m(l.custo_novo, 4)}</td>
                  <td style={{
                    ...td, ...num, textAlign: 'right', fontWeight: l.muda ? 700 : 400,
                    ...(l.muda && marcado ? { background: '#fde68a', color: '#000' } : {}),
                  }}>
                    <MoneyInput
                      valor={l.pix_novo}
                      onChangeValor={(v) => digitarPix(l, l.pix_calculado, v)}
                      title={l.digitado ? `Digitado por voce — o calculado era ${m(l.pix_calculado, 2)}. Apague para voltar a ele.` : 'Digite outro PIX para esta linha'}
                      style={{
                        width: 92, ...num, fontSize: 14, fontWeight: 700, textAlign: 'right', padding: '3px 6px',
                        border: l.digitado ? '2px solid #000' : '1px solid var(--panel-border)', borderRadius: 4,
                        background: '#fff', color: '#000',
                      }}
                    />
                    {l.digitado && <div style={{ fontSize: 9.5, fontWeight: 600, marginTop: 2 }}>digitado · calc. {m(l.pix_calculado, 2)}</div>}
                  </td>
                  {/* Lucro % (pedido dele, 07/10/2026) = a formula da planilha "Composicao de Preco"
                      dele, a mesma "margem" do rodape da familia (DA-643): (PIX novo / Custo novo) - 1.
                      Sai dos dois numeros EXIBIDOS nesta linha, pra ele conferir sem depender de mim —
                      e por isso mostra o efeito do arredondamento do PIX (23,10% num markup de 23%). */}
                  <td style={{ ...td, ...num, textAlign: 'right' }}>
                    {l.custo_novo > 0
                      ? `${((l.pix_novo / l.custo_novo - 1) * 100).toFixed(2).replace('.', ',')}%`
                      : '—'}
                  </td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <button type="button" title="Tirar este produto da regua"
                      onClick={() => void desvincular(l.vinculo_id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: 2 }}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {linhas.length === 0 && !carregando && (
              <tr>
                <td colSpan={12} style={{ padding: '16px 12px', textAlign: 'center', fontSize: 12, color: 'var(--panel-fg, #1f2937)' }}>
                  Nenhum produto vinculado ainda — busque abaixo o que esta familia precifica.
                  {principalOutraUnidade && produtoPrincipal && (
                    <div style={{ marginTop: 6 }}>
                      O produto principal <b>{produtoPrincipal.codigo_interno}</b> e vendido em <b>{principalOutraUnidade}</b>,
                      entao nao entrou sozinho: busque-o abaixo com o fator certo (rolo de 100m = 100).
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <BuscarProdutoParaRegua onEscolher={vincular} />
    </Modal>
  );
}

// Busca do produto que entra na regua. Dropdown em position:fixed medido do input: o corpo do modal
// e overflow-y auto e cortaria um dropdown absoluto (regra da casa).
function BuscarProdutoParaRegua({ onEscolher }: { onEscolher: (p: ProdutoBusca, fator: number) => void | Promise<void> }) {
  const dialog = useDialog();
  const [busca, setBusca] = useState('');
  const [fator, setFator] = useState(1);
  const [resultados, setResultados] = useState<ProdutoBusca[]>([]);
  const [pos, setPos] = useState<{ left: number; top: number; width: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (busca.trim().length < 2) { setResultados([]); return; }
    timer.current = setTimeout(async () => {
      try {
        const r = await api.get<ProdutoBusca[]>('/api/produtos', { query: { q: busca, com_inativos: '1' } });
        setResultados((r || []).slice(0, 10));
      } catch { setResultados([]); }
    }, 250);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [busca]);

  useEffect(() => {
    if (resultados.length === 0) { setPos(null); return; }
    const medir = () => {
      const el = inputRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setPos({ left: r.left, top: r.bottom + 2, width: Math.max(r.width, 360) });
    };
    medir();
    window.addEventListener('scroll', medir, true);
    window.addEventListener('resize', medir);
    return () => { window.removeEventListener('scroll', medir, true); window.removeEventListener('resize', medir); };
  }, [resultados.length]);

  const escolher = (p: ProdutoBusca) => {
    if (produtoInativo(p)) { void dialog.alerta(MSG_PRODUTO_INATIVO); return; }
    void onEscolher(p, fator);
    setBusca('');
    setResultados([]);
  };
  const teclado = useListaTeclado(resultados, escolher);

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
      <div style={{ position: 'relative', flex: 1, minWidth: 260, display: 'flex', alignItems: 'center' }}>
        <Search size={14} style={{ position: 'absolute', left: 9, color: 'var(--panel-fg, #6b7280)', pointerEvents: 'none' }} />
        <input
          ref={inputRef}
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          onKeyDown={teclado.onKeyDown}
          placeholder="Buscar produto por codigo ou descricao..."
          style={{ width: '100%', fontSize: 12.5, paddingLeft: 28 }}
        />
      </div>
      <label style={{ fontSize: 11.5, display: 'flex', alignItems: 'center', gap: 6 }}>
        Fator
        <input
          type="number" min={0.0001} step={1} value={fator}
          onChange={(e) => setFator(Number(e.target.value))}
          style={{ width: 76, ...num, fontSize: 12, padding: '4px 6px', textAlign: 'right' }}
        />
      </label>
      {pos && resultados.length > 0 && (
        <div style={{
          position: 'fixed', left: pos.left, top: pos.top, width: pos.width, zIndex: 1100,
          background: 'var(--panel-bg)', border: '1px solid var(--panel-border)', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)', maxHeight: 260, overflowY: 'auto',
        }}>
          {resultados.map((p, i) => (
            <button
              key={p.id}
              type="button"
              onClick={() => escolher(p)}
              style={{
                display: 'flex', gap: 8, width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
                padding: '7px 10px', fontSize: 12,
                background: i === teclado.highlight ? '#fde68a' : 'transparent',
                color: i === teclado.highlight ? '#000' : 'var(--panel-fg, inherit)',
              }}
            >
              <span style={{ ...num, fontWeight: 700 }}>{p.codigo_interno}</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{produtoInativo(p) && <SeloInativo />}{p.descricao}</span>
              <Plus size={13} style={{ marginLeft: 'auto', flexShrink: 0 }} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
